import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Section, SectionHeader } from './styles';
import TouristOffersBusinessService from 'services/TouristOffersBusinessService';
import RestaurantCategoryService from 'services/RestaurantCategoryService';
import TourCategoryService from 'services/TourCategoryService';
import { IBusiness } from 'interfaces/IBusinesses';
import CategoryTable from 'components/CategoryTable';
import Heading from 'components/Heading';
import Button from 'components/Button';
import ConfirmationModal from 'components/ConfirmationModal';
import eyeIcon from 'assets/images/eyeGrayIcon.svg';
import editIcon from 'assets/images/editIcon.svg';
import trashIcon from 'assets/images/trashIcon.svg';
import ExperienceCarouselConfig from './ExperienceCarouselConfig';

import { DataGrid, GridColDef, GridToolbar, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { Grid } from '@mui/material';

const Activity = (): JSX.Element => {
  const [activities, setActivities] = useState<IBusiness[]>([]);
  const [activityId, setActivityId] = useState(0);
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    id: false,
  });

  const history = useHistory();
  const activityService = new TouristOffersBusinessService();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const filteredActivities = await activityService.findAllSeturTrades();
    setActivities(filteredActivities);
  };

  const deleteActivity = async () => {
    await activityService.remove(activityId);
    setActivities(prev => prev.filter(item => item.id !== activityId));
  };

  const formatEventCreate = (dateString: string): JSX.Element => {
    if (!dateString) return <span>- - -</span>;

    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('pt-BR');
    const formattedTime = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return (
      <div className="createdDate">
        <span>{formattedDate}</span>
        <span>{formattedTime}</span>
      </div>
    );
  };

  const activityColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'createdAt',
      headerName: 'Criação',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventCreate(params.value as string),
    },
    {
      field: 'updatedAt',
      headerName: 'Última Edição',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => formatEventCreate(params.value as string),
    },
    {
      field: 'user',
      headerName: 'Cadastrado/Editado Por',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => params.row.user?.name || params.row.user?.email || '- - -',
    },
    {
      field: 'title',
      headerName: 'Título da Experiência Turística',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'experiences',
      headerName: 'Experiência Turística',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
      valueGetter: params => {
        const exps = params.row.experiences;
        if (Array.isArray(exps) && exps.length > 0) return exps.join(', ');
        return '- - -';
      },
    },
    {
      field: 'businessType',
      headerName: 'Classificação',
      flex: 2,
      minWidth: 250,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Ações',
      flex: 1,
      minWidth: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', width: '100%' }}>
          <button
            className="button-icon"
            title="Visualizar"
            onClick={() => history.push(`/admin/activity/${params.id}/preview`)}>
            <img src={eyeIcon} alt="visualizar" />
          </button>

          <button className="button-icon" title="Editar" onClick={() => history.push(`/admin/activities/${params.id}`)}>
            <img src={editIcon} alt="editar" />
          </button>

          <button
            className="button-icon"
            title="Excluir"
            onClick={() => {
              setActivityId(Number(params.id));
              setConfirmDeletion(true);
            }}>
            <img src={trashIcon} alt="excluir" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Container>
      <ExperienceCarouselConfig />
      <CategoryTable title="Categorias de Restaurantes" service={new RestaurantCategoryService()} />
      <CategoryTable title="Categorias de Passeios" service={new TourCategoryService()} />
      <Section>
        <SectionHeader>
          <Heading>Lista de Experiências Turísticas Cadastradas pela SETUR</Heading>
          <Button type="button" buttonType="primary" onClick={() => history.push('/admin/activities/new-activity')}>
            Adicionar
          </Button>
        </SectionHeader>

        <Grid sx={{ width: '100%', marginBottom: '50px' }}>
          <DataGrid
            sx={{
              background: '#fff',
              boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
              borderRadius: '18px',
              width: '100%',
              border: 'none',
            }}
            rows={activities}
            columns={activityColumns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                densitySelector: false,
                exportButton: false,
              },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Grid>
      </Section>

      <ConfirmationModal
        open={confirmDeletion}
        title="EXCLUSÃO DE ITEM"
        message="Você tem certeza de que deseja excluir esse item?"
        onCancel={() => setConfirmDeletion(false)}
        onConfirm={() => {
          setConfirmDeletion(false);
          deleteActivity();
        }}
      />
    </Container>
  );
};

export default Activity;
